import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import type { Card } from '../game/data/cards/types';
import { getTribeDefinition } from '../game/data/tribes';
import { keywordsForCard, triggersForCard, type Keyword } from '../game/data/keywords';
import { CardTile } from './CardTile';
import { useEffectiveReducedMotion } from './useReducedMotion';
import { EDGES } from '../theme/tokens';

// Card lightbox — the canonical deep view of a card.
//
// docs/tooltip-audit.md §5 calls for promoting HandRail's tap-preview into a
// shared CardDetail showing "what size='preview' shows today PLUS keyword
// lines, flavor text, tribe verb + fantasy". This is that.
//
// It surfaces three things the game already authored and never rendered:
//   - Card.flavorText          (55 neutral units + 2 tokens, zero references)
//   - TribeDefinition.verb     (comment claimed it was on the synergy pip)
//   - TribeDefinition.fantasy  (one line of player-language "what this does")
// plus keyword and trigger rules from the new glossary.
//
// Above everything in the tavern: the hand rail sits at 45, discover/scouting
// at 50, the triple flourish at 60. This is player-invoked and must never open
// underneath any of them.
const DETAIL_Z = 70;

/** Longest edge of the art panel. Art is authored ~1024px, so this is a
    downscale on every real viewport rather than an upscale. */
const ART_MAX = 420;

export function CardDetail({
  card,
  onClose,
}: {
  card: Card;
  onClose: () => void;
}) {
  const reducedMotion = useEffectiveReducedMotion();
  const tribe = card.tribe ? getTribeDefinition(card.tribe) : undefined;
  const keywords = keywordsForCard(card);
  const triggers = triggersForCard(card);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (typeof document === 'undefined') return null;

  const artSrc = card.art ? `${import.meta.env.BASE_URL}${card.art}` : null;
  const tribeColor = tribe ? `#${tribe.color.toString(16).padStart(6, '0')}` : '#8a7e6c';
  const factionFont = card.faction === 'emo' ? 'font-emo' : 'font-cholo';

  return createPortal(
    <motion.div
      key="card-detail"
      role="dialog"
      aria-modal="true"
      aria-label={`${card.name} details`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: reducedMotion ? 0 : 0.16 }}
      onClick={onClose}
      className="pt-safe pb-safe fixed inset-0 flex items-center justify-center overflow-y-auto bg-asphalt/85 p-md sm:p-lg"
      style={{ zIndex: DETAIL_Z }}
    >
      <motion.div
        // Stop propagation so clicks inside don't dismiss — only the
        // backdrop and the explicit close button do.
        onClick={(e) => e.stopPropagation()}
        initial={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.94, y: 14 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={reducedMotion ? { opacity: 0 } : { opacity: 0, scale: 0.97 }}
        transition={{ duration: reducedMotion ? 0 : 0.2, ease: 'easeOut' }}
        className="my-auto flex w-full max-w-[860px] flex-col gap-md rounded-md border bg-stucco1 p-md sm:p-lg lg:flex-row lg:gap-lg"
        style={{ borderColor: EDGES.soft }}
      >
        {/* Art / card panel. Real art gets the space; without it the
            procedural card tile is the visual, so we show that instead of
            an empty frame. */}
        <div className="flex shrink-0 flex-col items-center gap-sm lg:w-[300px]">
          {artSrc ? (
            <div
              className="relative w-full overflow-hidden rounded-md border"
              style={{ borderColor: 'rgba(255,138,61,0.25)', maxHeight: ART_MAX }}
            >
              <img
                src={artSrc}
                alt={card.name}
                className="h-full w-full object-cover object-top"
                style={{ maxHeight: ART_MAX }}
              />
              {/* Stat corners over the art, mirroring the card's own
                  ATK/HP placement so the mapping is obvious. */}
              <span className="absolute bottom-[6px] left-[6px] inline-flex h-8 min-w-[32px] items-center justify-center rounded-sm bg-aerosolRed px-xs font-data text-[16px] font-bold text-bone">
                {card.attack + (card.combatBuff?.attack ?? 0)}
              </span>
              <span className="absolute bottom-[6px] right-[6px] inline-flex h-8 min-w-[32px] items-center justify-center rounded-sm bg-glowStick px-xs font-data text-[16px] font-bold text-sharpie">
                {card.health + (card.combatBuff?.health ?? 0)}
              </span>
            </div>
          ) : (
            <div className="w-[200px] max-w-full">
              <CardTile card={card} size="preview" />
            </div>
          )}
        </div>

        {/* Text column */}
        <div className="flex min-w-0 flex-1 flex-col gap-sm">
          <div className="flex items-start justify-between gap-sm">
            <div className="min-w-0">
              <p className="font-data text-[10px] uppercase tracking-[0.3em] text-marker">
                tier {card.tier}
                {card.isGolden ? ' · golden' : ''}
              </p>
              <h2
                className={`mt-[2px] break-words text-[26px] leading-tight sm:text-[30px] ${factionFont}`}
                style={{ color: tribeColor }}
              >
                {card.name}
              </h2>
              <p className="mt-[2px] font-data text-[10px] uppercase tracking-[0.25em] text-graphite">
                {tribe ? tribe.name : 'no tribe'}
                {' · '}
                {card.faction === 'neutral' ? 'neutral' : card.faction}
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="shrink-0 rounded-sm border px-sm py-[2px] font-data text-[14px] leading-none text-graphite hover:bg-stucco2 hover:text-bone"
              style={{ borderColor: EDGES.faint }}
            >
              ✕
            </button>
          </div>

          {/* Rules text as authored on the card. */}
          {card.text && (
            <p className="text-[14px] leading-snug text-bone">{card.text}</p>
          )}

          {/* Keyword + trigger rules — the glossary the game never had. */}
          {(keywords.length > 0 || triggers.length > 0) && (
            <div
              className="flex flex-col gap-xs rounded-md border bg-stucco2/50 p-sm"
              style={{ borderColor: EDGES.faint }}
            >
              <p className="font-data text-[9px] uppercase tracking-[0.3em] text-marker">
                how it works
              </p>
              {triggers.map((k) => (
                <KeywordLine key={`t-${k.name}`} keyword={k} tone="sodium" />
              ))}
              {keywords.map((k) => (
                <KeywordLine key={`f-${k.name}`} keyword={k} tone="ice" />
              ))}
            </div>
          )}

          {/* Tribe identity — verb + fantasy, both previously unrendered. */}
          {tribe && (
            <div
              className="flex flex-col gap-[2px] rounded-md border p-sm"
              style={{
                borderColor: `${tribeColor}55`,
                background: `${tribeColor}12`,
              }}
            >
              <p className="font-data text-[9px] uppercase tracking-[0.3em] text-marker">
                {tribe.name} · <span style={{ color: tribeColor }}>{tribe.verb}</span>
              </p>
              <p className="text-[13px] leading-snug text-bone">{tribe.fantasy}</p>
            </div>
          )}

          {/* Flavor last — it's colour, not information. */}
          {card.flavorText && (
            <p className="mt-auto border-t pt-sm text-[13px] italic leading-snug text-marker"
               style={{ borderColor: EDGES.faint }}>
              “{card.flavorText}”
            </p>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

function KeywordLine({ keyword, tone }: { keyword: Keyword; tone: 'sodium' | 'ice' }) {
  return (
    <p className="text-[12px] leading-snug text-marker">
      <span
        className={`font-data text-[11px] uppercase tracking-[0.15em] ${
          tone === 'sodium' ? 'text-sodium' : 'text-iceCrystal'
        }`}
      >
        {keyword.name}
      </span>
      <span className="text-graphite"> — </span>
      {keyword.rule}
    </p>
  );
}
